import { useAtomValue } from "jotai";
import {
    incomeStatementMetricsAtom,
    incomeStatementResolutionAtom,
} from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { Metric } from "@/entities/Finance/Finance.model";
import dayjs from "dayjs";
import { getMetric } from "../../utils/getMetric";
import { filterPeriodsByWindow } from "../../utils/filterPeriodsByWindow";
import { applyWindowToMetrics } from "../../utils/applyWindowToMetric";

export interface SummaryTableRow {
    metric: string;
    metricLabel?: string;
    yoyChange?: number | null;
    [key: string]: string | number | (() => void) | undefined | null;
    onClick?: () => void;
}

interface PeriodColumn {
    field: string;
    headerName: string;
}

export interface SummaryIncomeStatementResult {
    data: SummaryTableRow[];
    columns: PeriodColumn[];
}

function formatPeriodHeader(period: string): string {
    const date = dayjs(period);
    if (!date.isValid()) return period;

    if (period.length === 4) {
        return period;
    } else if (period.includes("Q")) {
        return period.replace("-", "-").slice(0, -2);
    } else {
        return date.format("MM-YY");
    }
}

function calculateYoYDelta(
    firstValue: number | null,
    lastValue: number | null,
): number | null {
    if (firstValue === null || lastValue === null || firstValue === 0) {
        return null;
    }
    const delta = (lastValue - firstValue) / Math.abs(firstValue);
    return delta;
}

export function useSummaryIncomeStatementData(): SummaryIncomeStatementResult {
    const incomeStatementMetrics = useAtomValue(incomeStatementMetricsAtom);
    const metricResolution = useAtomValue(incomeStatementResolutionAtom);
    const windowedMetrics = applyWindowToMetrics(
        incomeStatementMetrics,
        metricResolution.window,
    );

    const revenue = getMetric(
        windowedMetrics,
        AccountLabel.REVENUES,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
    );
    const grossMargin = getMetric(
        windowedMetrics,
        AccountLabel.GROSS_MARGIN,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
    );
    const ebitda = getMetric(
        windowedMetrics,
        AccountLabel.EBITDA,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
    );
    const ebitdaMargin = getMetric(
        windowedMetrics,
        AccountLabel.EBITDA_MARGIN,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
    );
    const salesAndMarketingEfficiency = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
    );
    const netIncome = getMetric(
        windowedMetrics,
        AccountLabel.NET_INCOME,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
    );
    const netIncomeMargin = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
    );

    const allPeriods = new Set<string>();
    [
        revenue,
        grossMargin,
        ebitda,
        ebitdaMargin,
        salesAndMarketingEfficiency,
        netIncome,
        netIncomeMargin,
    ].forEach((metric) => {
        metric?.values.forEach((v) => allPeriods.add(v.period));
    });

    const sortedPeriods = Array.from(allPeriods).sort();

    const filteredPeriods = filterPeriodsByWindow(
        sortedPeriods,
        metricResolution.window,
    );

    const periodColumns: PeriodColumn[] = filteredPeriods.map((period) => ({
        field: period,
        headerName: formatPeriodHeader(period),
    }));

    const columns: PeriodColumn[] = [
        { field: "metric", headerName: "" },
        ...periodColumns,
        { field: "yoyChange", headerName: "% Δ YoY" },
    ];

    function createRow(
        label: string,
        metric: Metric | undefined,
        fieldType: FinanceLabFieldType,
        metricLabel?: string,
    ): SummaryTableRow {
        const row: SummaryTableRow = {
            metric: label,
            metricLabel,
        };

        if (!metric) {
            filteredPeriods.forEach((period) => {
                row[period] = "-";
            });
            row.yoyChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            metric.values.map((v) => [v.period, v.value]),
        );

        filteredPeriods.forEach((period) => {
            const value = valuesByPeriod.get(period);
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, fieldType)
                    : "-";
        });

        const firstValue = valuesByPeriod.get(filteredPeriods[0]) ?? null;
        const lastValue =
            valuesByPeriod.get(filteredPeriods[filteredPeriods.length - 1]) ??
            null;
        row.yoyChange = calculateYoYDelta(firstValue, lastValue);

        return row;
    }

    const data: SummaryTableRow[] = [
        createRow(
            "Revenue",
            revenue,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.REVENUES,
        ),
        createRow(
            "Gross Margin %",
            grossMargin,
            FinanceLabFieldType.PERCENTAGE,
            AccountLabel.GROSS_MARGIN,
        ),
        createRow(
            "EBITDA",
            ebitda,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.EBITDA,
        ),
        createRow(
            "EBITDA Margin %",
            ebitdaMargin,
            FinanceLabFieldType.PERCENTAGE,
            AccountLabel.EBITDA_MARGIN,
        ),
        createRow(
            "Sales & Marketing Efficiency",
            salesAndMarketingEfficiency,
            FinanceLabFieldType.MULTIPLE,
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
        ),
        createRow(
            "Net Income",
            netIncome,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.NET_INCOME,
        ),
        createRow(
            "Net Income Margin %",
            netIncomeMargin,
            FinanceLabFieldType.PERCENTAGE,
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
        ),
    ];

    return { data, columns };
}
